import { darken, mix, transparentize } from "polished";
import styled, { DefaultTheme } from "styled-components";

import { T_HEADER_bold, T_PLAIN_big, T_PLAIN_small } from "../../global.style";
import { TextEllipsisStyled } from "../textEllipsis/TextEllipsis.styles";
import { TileSize } from "./Calendar.utils";

const GRID_GAP = 2;
const ACTIONS_WRAPPER_PADDING = 5;

export const CALENDAR_MAX_WIDTH = 7 * TileSize.MAX + 6 * GRID_GAP;
export const MAX_HEIGHT_BREAKPOINT = 7 * TileSize.BREAK_L + 6 * GRID_GAP;

export const DayLabel = styled.div`
    ${T_HEADER_bold};
    text-transform: uppercase;
`;

export const CalendarWrapper = styled.div`
    display: grid;
    container: calendarWrapper / inline-size;
    grid-template-columns: repeat(7, 1fr);
    grid-auto-rows: min-content;
    row-gap: ${GRID_GAP}px;
    column-gap: ${GRID_GAP}px;
    width: 100%;
    max-width: ${CALENDAR_MAX_WIDTH}px;

    ${DayLabel} {
        padding-bottom: 6px;
        padding-left: 10px;
    }
`;

export const DayWrapper = styled.div<{
    $isSelected?: boolean;
    $isDummy?: boolean;
    $isHoliday?: boolean;
    $isFocused?: boolean;
    $isDisabled?: boolean;
}>`
    container: dayWrapper / inline-size;
    display: flex;
    position: relative;
    min-width: ${TileSize.MIN}px;
    max-width: ${TileSize.MAX}px;
    max-height: ${TileSize.BREAK_L}px;
    flex-direction: column;
    
    @container calendarWrapper (width <= ${MAX_HEIGHT_BREAKPOINT}px) {
        aspect-ratio: 1 / 1;
    }
    
    @container calendarWrapper (width > ${MAX_HEIGHT_BREAKPOINT}px) {
        height: ${TileSize.BREAK_L}px;
    }
    
    border-radius: 3px;
    box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.05);
    border: 1px solid ${props => props.$isSelected || props.$isFocused ? props.theme.C_ACT_main : "transparent"};
    cursor: ${props => props.$isDummy ? "inherit" : "pointer"};
    user-select: none;
    padding: 10px 5px 3px;

    background: ${props => props.$isSelected || props.$isFocused ? props.theme.C_BG_tile_selected : props.theme.C_BG_tile_front};

    &:nth-child(7n + 13), // saturdays
    &:nth-child(7n + 14) { // sundays
        background: ${props => !props.$isDummy && !props.$isSelected && !props.$isFocused && !props.$isHoliday ? mix(0.03, props.theme.C_ACT_main, props.theme.C_BG_tile_front) : ""};
    }

    ${props => props.$isHoliday ? `
        background: ${!props.$isSelected && !props.$isFocused ? mix(0.06, props.theme.C_ACT_main, props.theme.C_BG_tile_front) : ""};
    ` : ""}
    
    &:hover, &:focus-visible {
        ${props => !props.$isDummy ? `
            border: 1px solid ${transparentize(0.5, props.theme.C_ACT_main)};
            outline: none;
        ` : ""};
    }
    
    ${props => props.$isFocused ? `
        &::after {
          content: '';
          position: absolute;
          top: 1px;
          left: 1px;
          right: 1px;
          bottom: 1px;
          border: 1px dotted ${props.theme.C_ACT_main};
          pointer-events: none;
        }
    ` : ""}
    
    ${props => props.$isDisabled ? `
        opacity: 0.5;
        pointer-events: none;
    ` : ""}
`;

export const DayHeaderWrapper = styled.div`
    display: flex;
    justify-content: left;
    align-items: baseline;
    padding: 0 5px;
    
    @container dayWrapper (width >= 80px) {
        justify-content: space-between;
        padding: 0 5px 0 11px;    
    }
    @container dayWrapper (width >= 88px) {
        padding: 0 8px 0 12px;
    }
    @container dayWrapper (width >= 104px) {
        padding: 0 13px 0 16px;
`;

export const DayIndex = styled.div<{
    $isToday: boolean;
}>`
    ${T_HEADER_bold};
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1;
    margin-bottom: 6px;
    
    @container dayWrapper (width < 80px) {
        width: 18px;
    }
        
    @container dayWrapper (width >= 80px) {
        width: 22px;
        &::before {
            left: -6px;
        }        
    }
    ${props => props.$isToday ? `
        color: ${props.theme.C_BTN_4D_emph_text};
        &::before {
            content: "";
            position: absolute;
            z-index: -1;
            background: ${props.theme.C_ACT_main};
            border-radius: 100%;
            width: 32px;
            height: 32px;
            top: -5px;
        }
    ` : ""};
`;

export const WorkTimeWrapper = styled.div`
    font-size: 10px;
    @container dayWrapper (width < 80px) {
        display: none;
    }
    @container dayWrapper (width >= 88px) {
        font-size: 11px;
    }
    
    @container dayWrapper (width >= 104px) {
        font-size: 12px;
    }
    color: ${props => transparentize(0.5, props.theme.C_TEXT_primary)};
    text-align: center;
`;

export const ActionsRow = styled.div`
    display: flex;
    gap: 5px;
    width: 100%;
    justify-content: center;
    @container dayWrapper (width >= 80px) {
        flex-grow: 1;
        min-height: 16px;
        max-height: 20px;
    }
    @container dayWrapper (width < 80px) {
        height: 10px;
        align-items: flex-end;
        &:first-of-type {
            display: none;
        }
    }
`;

export const ActionsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    justify-content: flex-start;
    flex-grow: 1;
    @container dayWrapper (width < 80px) {
        justify-content: flex-end;
        padding-bottom: 4px;
    }
`;

export const ActionTitle = styled.div`
    margin-left: ${ACTIONS_WRAPPER_PADDING}px;
    overflow: hidden;
    display: flex;
    align-items: center;

    ${TextEllipsisStyled} {
        ${T_PLAIN_small};
    }
    
    @container dayWrapper (width < 80px) {
        display: none;
    }
`;

interface IStyledActionProps {
    $color: keyof DefaultTheme;
    $isStart: boolean;
    $isEnd: boolean;
    $hasConflict?: boolean;
    $dayHasMoreActions?: boolean;
    $isPlaceholder?: boolean;
    $isMonday?: boolean;
}

const getWidthForAction = (props: IStyledActionProps): string => {
    if (props.$dayHasMoreActions) {
        return "auto";
    }
    let extraWidth = 0;
    if (!props.$isEnd) {
        extraWidth += GRID_GAP + ACTIONS_WRAPPER_PADDING + 1;
    }
    if (!props.$isStart && !props.$isMonday) {
        extraWidth += GRID_GAP + ACTIONS_WRAPPER_PADDING + 1;
    }
    return extraWidth ? `calc(100% + ${extraWidth}px)` : "100%";
};

export const Circle = styled.div<{
    $isHalf: boolean;
}>`
    height: 10px;
    min-width: ${props => props.$isHalf ? "5px" : "10px"};
    margin-right: ${props => props.$isHalf ? "5px" : "0"};
    background-color: ${props => props.theme.C_BTN_4D_emph_text};
    border: 1px solid ${props => props.theme.C_BTN_4D_emph_text};
    border-radius: ${props => props.$isHalf ? "10px 0 0 10px" : "100%"};
`;

export const MoreButton = styled.div`
    ${T_PLAIN_big};
    @container dayWrapper (width < 80px) {
        ${T_PLAIN_small};
    }
    color: ${props => props.theme.C_ACT_main};

    &:hover {
        color: ${props => props.theme.C_BTN_4L_emph_text_hover};
    }
`;

export const Action = styled.div<IStyledActionProps>`
    display: inline-flex;
    color: ${props => props.$isPlaceholder ? props.theme[props.$color] : props.theme.C_BTN_4D_emph_text};
    height: 20px;
    width: ${props => getWidthForAction(props)};
    ${T_PLAIN_big};
    align-items: center;
    justify-content: ${props => props.$hasConflict ? "center" : "flex-start"};
    z-index: 1;
    position: relative;

    padding: 0 ${props => props.$isEnd ? "5" : "10"}px 0 ${props => props.$isStart ? "5" : "10"}px;

    ${props => props.$isStart || props.$isMonday ? `
        border-bottom-left-radius: 3px; 
        border-top-left-radius: 3px;
    ` : ""}
    
    border-top-right-radius: ${props => props.$isEnd ? "3px" : "0"};
    border-bottom-right-radius: ${props => props.$isEnd ? "3px" : "0"};

    background: ${props => props.$hasConflict ? `
        repeating-linear-gradient(
                -45deg,
                ${props.theme[props.$color]} 0 8px,
                ${darken(0.15, props.theme[props.$color])} 8px 16px
        )` : props.$isPlaceholder ?
            mix(0.2, props.theme[props.$color], props.theme.C_BG_floorplan) : props.theme[props.$color]} fixed;

    flex-grow: 1;

    @container dayWrapper (width < 80px) {
        height: 10px;
        width: 10px;
        padding: 0;
        background: none;
        
        flex-grow: 0;
        & > ${Circle} { 
            background-color: ${props => props.$isPlaceholder ? mix(0.2, props.theme[props.$color], props.theme.C_BG_floorplan) : props.theme[props.$color]};
            border-color: ${props => props.$isPlaceholder ? mix(0.2, props.theme[props.$color], props.theme.C_BG_floorplan) : props.theme[props.$color]};
        }
        & + ${MoreButton} { 
            height: 10px;
            display: flex;
            align-items: center;
        }
        
        & .SI_4L_fill_orange {
            fill: ${props => darken(0.15, props.theme[props.$color])};
        }
    }
    
    @container dayWrapper (width >= 80px) {    
        ${props => props.$isStart || props.$isMonday ? "" : `margin-left: -${GRID_GAP + ACTIONS_WRAPPER_PADDING + 1}px;`}
        ${props => props.$isEnd ? "" : `margin-right: -${GRID_GAP + ACTIONS_WRAPPER_PADDING + 1}px;`}

        & .SI_4L_fill_orange {
            fill: ${props => props.theme.C_BTN_4D_emph_text};
        }
        
        ${props => props.$isPlaceholder ? `
            & > ${Circle} {     
                display: none;
            }
        ` : ""}
        
        ${props => props.$isPlaceholder && props.$isStart ? `
            &:before {
                content: "";
                width: 6px;
                height: 100%;
                position: absolute;
                left: 0;
                border-radius: 3px 0 0 3px;
                background: ${props.theme[props.$color]};  
            }
        ` : ""}
     }
`;